import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material"
import CardIcon from "@mui/icons-material/CreditCardOutlined"
import LockIcon from "@mui/icons-material/LockOutlined"

import { billingApi } from "../../api"
import { formatDate } from "../../utils/format"
import { MOTION, transition } from "../../theme"

/**
 * The card a client authorises us to charge.
 *
 * ── What this component is not ──────────────────────────────────────────────
 *
 * It is not a card form, and there is deliberately nowhere on this screen to
 * type a number. The button opens Stripe's own page; what comes back to us is a
 * reference and four digits. No card number, expiry or security code passes
 * through this app or reaches this database — which is the difference between
 * integrating a payment processor and becoming one, and it is worth a whole
 * component's worth of indirection to keep.
 *
 * ── Why authorising is a separate act from paying ───────────────────────────
 *
 * Stripe is asked for a `setup` session rather than a payment: agreeing that we
 * may charge a card in future is not the same as being charged today, and
 * nobody should have to be billed in order to agree to be billed.
 */
export default function AutopayCard({ clientId, clientLabel, onChanged, sx }) {
  const [state, setState] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const load = useCallback(async () => {
    try {
      setState(await billingApi.autopay(clientId ? { client_id: clientId } : {}))
    } catch (err) {
      setError(err.userMessage || "Could not read the payment settings")
    }
  }, [clientId])

  useEffect(() => {
    load()
  }, [load])

  /*
   * Stripe's page is a full-page redirect, not a popup.
   *
   * A popup is blocked often enough that the failure would be invisible — the
   * button would appear to do nothing — and the return trip carries a query
   * parameter this app reads on the way back in.
   */
  const connect = async () => {
    setBusy(true)
    setError(null)
    try {
      const { url } = await billingApi.autopaySession(clientId ? { client_id: clientId } : {})
      window.location.assign(url)
    } catch (err) {
      setError(err.userMessage || "Could not open the card form")
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await billingApi.removeAutopay(clientId ? { client_id: clientId } : {})
      setNotice(result.message || "Card removed.")
      await load()
      onChanged?.()
    } catch (err) {
      setError(err.userMessage || "Could not remove the card")
    } finally {
      setBusy(false)
    }
  }

  if (!state) {
    return (
      <Card sx={sx}>
        <CardContent>
          <CircularProgress size={20} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={sx}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <CardIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="h3" sx={{ flex: 1 }}>
            Automatic payment
          </Typography>
          <Chip
            size="small"
            label={state.authorized ? "On" : "Off"}
            color={state.authorized ? "success" : "default"}
            variant={state.authorized ? "filled" : "outlined"}
            sx={{ height: 22, transition: transition("background-color", MOTION.base) }}
          />
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        ) : null}

        {!state.configured ? (
          <Alert severity="info">
            Card payments are not set up on this deployment yet, so nothing can be authorised. Invoices still arrive by
            email and can be paid from the link on them.
          </Alert>
        ) : state.authorized ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Recurring charges for {clientLabel || "this account"} are taken from{" "}
              <strong>
                {state.card?.brand || "the card"} ending {state.card?.last4 || "····"}
              </strong>
              {state.authorized_at ? ` — authorised ${formatDate(state.authorized_at)}` : ""}. An invoice is sent every
              time, before and after the money moves.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" onClick={connect} disabled={busy}>
                Use a different card
              </Button>
              <Button size="small" color="error" onClick={remove} disabled={busy}>
                {busy ? "Removing…" : "Stop automatic payment"}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Authorise a card and recurring charges are collected on the day they fall due, with an invoice each time.
              Without one, every invoice is emailed for you to pay by hand.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <LockIcon />}
              onClick={connect}
              disabled={busy}
            >
              {busy ? "Opening Stripe…" : "Authorise a card"}
            </Button>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                The card is entered on Stripe's own page. TallyWise never sees or stores the number.
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}
